/**
 * Flatten a {@link CapabilitySuggestion} into the ordered list of
 * independently-selectable diff nodes (Phase F-finale-4 Slice 5).
 *
 * The diff drawer renders a tree, but selection is flat: every stage,
 * requirement, and bundled field is its own toggleable node keyed by the
 * server-assigned `node_id`. Validity (and any blocking module) is resolved
 * from the suggestion's `validation_report` — a node missing from the report
 * is treated as valid (the BE only writes entries for nodes it could check).
 */

import type {
  CapabilitySuggestion,
  FieldSuggestion,
  FunnelSuggestion,
  NodeValidation,
  RequirementSuggestion,
  StageSuggestion,
} from "@eduviaos/shared";

/** One selectable node in the diff drawer. */
export interface SelectableNode {
  node_id: string;
  /** `false` disables the checkbox (invalid reference / disabled module). */
  isValid: boolean;
  /** Tooltip shown on a disabled checkbox. */
  invalidReason?: string;
  /** Module slug blocking this node, for the 🚫 badge. */
  requiresModule?: string;
}

function validityFor(
  nodeId: string,
  reports: Map<string, NodeValidation>,
  fallbackModule?: string | null,
): Omit<SelectableNode, "node_id"> {
  const report = reports.get(nodeId);
  const requiresModule = report?.requires_module ?? fallbackModule ?? undefined;
  return {
    isValid: report?.is_valid ?? true,
    invalidReason: report?.reason ?? undefined,
    requiresModule: requiresModule ?? undefined,
  };
}

function fromRequirement(
  req: RequirementSuggestion,
  reports: Map<string, NodeValidation>,
): SelectableNode {
  return { node_id: req.node_id, ...validityFor(req.node_id, reports, req.requires_module) };
}

function fromField(field: FieldSuggestion, reports: Map<string, NodeValidation>): SelectableNode {
  return { node_id: field.node_id, ...validityFor(field.node_id, reports, field.requires_module) };
}

function fromStage(stage: StageSuggestion, reports: Map<string, NodeValidation>): SelectableNode[] {
  return [
    { node_id: stage.node_id, ...validityFor(stage.node_id, reports, stage.requires_module) },
    ...stage.requirements_for_stage.map((req) => fromRequirement(req, reports)),
  ];
}

export function collectNodes(suggestion: CapabilitySuggestion): SelectableNode[] {
  const reports = new Map<string, NodeValidation>(
    suggestion.validation_report.map((v) => [v.node_id, v]),
  );
  const payload = suggestion.parsed_payload;

  switch (suggestion.target_kind) {
    case "funnel": {
      const funnel = payload as FunnelSuggestion;
      return [
        ...funnel.stages.flatMap((stage) => fromStage(stage, reports)),
        ...funnel.fields.map((field) => fromField(field, reports)),
      ];
    }
    case "stage":
      return fromStage(payload as StageSuggestion, reports);
    case "requirement":
      return [fromRequirement(payload as RequirementSuggestion, reports)];
    case "field":
      return [fromField(payload as FieldSuggestion, reports)];
  }
}
